import { useCallback, useRef } from 'react'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../../constants/enrollmentContent'
import type { HydrateEnrollmentSessionFn } from '../../types'
import { openDocusignSigningSession } from './openDocusignSigningSession'
import { refreshDocusignSignatureStatus } from './refreshDocusignSignatureStatus'

type UseDocusignSignatureFlowArgs = {
  enrollmentSessionToken: string | null
  hydrateEnrollmentSession: HydrateEnrollmentSessionFn
  navigate: (to: string, options?: { replace?: boolean }) => void
  goToStep: (step: number) => void
  setStepSubmissionError: (value: string | null) => void
  setSignatureStatus: (value: string | null) => void
  setIsPreparingDocusign: (value: boolean) => void
  setIsCheckingSignature: (value: boolean) => void
  setIsFinalizingDocusign: (value: boolean) => void
  setIsDocusignIframeLoading: (value: boolean) => void
  isPreparingDocusignRef: { current: boolean }
  isRestartingDocusignRef: { current: boolean }
  resetDocusignSessionState: () => void
  setPreparingDocusignState: () => void
  completeSigningFlow: () => Promise<void>
  openDocusignModalWithUrl: (signingUrl: string) => void
}

export function useDocusignSignatureFlow({
  enrollmentSessionToken,
  hydrateEnrollmentSession,
  navigate,
  goToStep,
  setStepSubmissionError,
  setSignatureStatus,
  setIsPreparingDocusign,
  setIsCheckingSignature,
  setIsFinalizingDocusign,
  setIsDocusignIframeLoading,
  isPreparingDocusignRef,
  isRestartingDocusignRef,
  resetDocusignSessionState,
  setPreparingDocusignState,
  completeSigningFlow,
  openDocusignModalWithUrl
}: UseDocusignSignatureFlowArgs) {
  const activeSignatureRefreshRef = useRef<Promise<boolean> | null>(null)

  const handleOpenDocusignModal = useCallback(async () => {
    if (isPreparingDocusignRef.current) {
      return
    }

    if (!enrollmentSessionToken) {
      setStepSubmissionError(ENROLLMENT_COMMON_MESSAGES.sessionExpired)
      navigate('/application', { replace: true })
      return
    }

    isPreparingDocusignRef.current = true
    setIsPreparingDocusign(true)
    setStepSubmissionError(null)
    resetDocusignSessionState()
    isRestartingDocusignRef.current = false

    try {
      await openDocusignSigningSession({
        enrollmentSessionToken,
        hydrateEnrollmentSession,
        goToStep,
        setStepSubmissionError,
        setSignatureStatus,
        setPreparingDocusignState,
        isRestartingDocusignRef,
        completeSigningFlow,
        openDocusignModalWithUrl
      })
    } finally {
      isPreparingDocusignRef.current = false
      setIsPreparingDocusign(false)
    }
  }, [
    completeSigningFlow,
    enrollmentSessionToken,
    goToStep,
    hydrateEnrollmentSession,
    isPreparingDocusignRef,
    isRestartingDocusignRef,
    navigate,
    openDocusignModalWithUrl,
    resetDocusignSessionState,
    setIsPreparingDocusign,
    setPreparingDocusignState,
    setSignatureStatus,
    setStepSubmissionError
  ])

  const refreshSignatureStatus = useCallback(async () => {
    if (!enrollmentSessionToken) {
      return false
    }

    if (activeSignatureRefreshRef.current) {
      return await activeSignatureRefreshRef.current
    }

    setIsCheckingSignature(true)

    const refreshPromise = (async () => {
      try {
        return await refreshDocusignSignatureStatus({
          enrollmentSessionToken,
          hydrateEnrollmentSession,
          handleOpenDocusignModal,
          isRestartingDocusignRef,
          setSignatureStatus,
          setIsFinalizingDocusign,
          setIsDocusignIframeLoading,
          completeSigningFlow
        })
      } finally {
        activeSignatureRefreshRef.current = null
        setIsCheckingSignature(false)
      }
    })()

    activeSignatureRefreshRef.current = refreshPromise
    return await refreshPromise
  }, [
    completeSigningFlow,
    enrollmentSessionToken,
    handleOpenDocusignModal,
    hydrateEnrollmentSession,
    isRestartingDocusignRef,
    setIsCheckingSignature,
    setIsDocusignIframeLoading,
    setIsFinalizingDocusign,
    setSignatureStatus
  ])

  return {
    handleOpenDocusignModal,
    refreshSignatureStatus
  }
}
